import * as React from "react";

import { cn } from "@/lib/utils";

import { Icon } from "../Icon";
import { Input, type InputProps } from "../Input";

type FieldControlProps = {
  id?: string;
  required?: boolean;
  "aria-describedby"?: string;
  "aria-invalid"?: React.AriaAttributes["aria-invalid"];
};

export interface FormFieldProps {
  children: React.ReactElement<FieldControlProps>;
  className?: string;
  controlId?: string;
  description?: React.ReactNode;
  error?: React.ReactNode;
  label: React.ReactNode;
  labelClassName?: string;
  required?: boolean;
}

const FormField = ({
  children,
  className,
  controlId,
  description,
  error,
  label,
  labelClassName,
  required,
}: FormFieldProps) => {
  const generatedId = React.useId();
  const resolvedControlId =
    children.props.id ?? controlId ?? `field-${generatedId}`;
  const descriptionId = description
    ? `${resolvedControlId}-description`
    : undefined;
  const errorId = error ? `${resolvedControlId}-error` : undefined;
  const describedBy = [
    children.props["aria-describedby"],
    errorId ?? descriptionId,
  ]
    .filter(Boolean)
    .join(" ");

  const control = React.cloneElement(children, {
    id: resolvedControlId,
    required: children.props.required ?? required,
    "aria-describedby": describedBy || undefined,
    "aria-invalid":
      children.props["aria-invalid"] ?? (Boolean(error) || undefined),
  });

  return (
    <div className={cn("w-full", className)}>
      <label
        htmlFor={resolvedControlId}
        className={cn(
          "text-foreground mb-1.5 block text-sm font-normal",
          labelClassName,
        )}
      >
        {label}
        {required ? (
          <span aria-hidden="true" className="text-error ms-1">
            *
          </span>
        ) : null}
      </label>
      {control}
      {error ? (
        <p id={errorId} role="alert" className="text-error mt-1 text-sm">
          {error}
        </p>
      ) : description ? (
        <p id={descriptionId} className="text-muted-foreground mt-1 text-sm">
          {description}
        </p>
      ) : null}
    </div>
  );
};

export interface TextAreaProps extends Omit<
  React.ComponentProps<"textarea">,
  "size"
> {
  containerClassName?: string;
  description?: React.ReactNode;
  error?: React.ReactNode;
  label?: React.ReactNode;
  labelClassName?: string;
}

const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      className,
      containerClassName,
      description,
      error,
      id,
      label,
      labelClassName,
      rows = 4,
      ...props
    },
    ref,
  ) => {
    const textarea = (
      <textarea
        id={id}
        ref={ref}
        rows={rows}
        className={cn(
          "border-input bg-background text-foreground placeholder:text-muted-foreground focus-visible:ring-ring focus-visible:ring-offset-background read-only:bg-muted read-only:text-muted-foreground min-h-24 w-full resize-none rounded-sm border px-3 py-2 text-sm shadow-sm transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none forced-colors:border-[FieldText]",
          className,
        )}
        {...props}
      />
    );

    if (!label && !description && !error) {
      return textarea;
    }

    return (
      <FormField
        className={containerClassName}
        description={description}
        error={error}
        label={label ?? ""}
        labelClassName={labelClassName}
      >
        {textarea}
      </FormField>
    );
  },
);
TextArea.displayName = "TextArea";

export interface PasswordFieldProps extends Omit<
  InputProps,
  "rightIcon" | "type"
> {
  hideLabel: string;
  showLabel: string;
}

const PasswordField = React.forwardRef<HTMLInputElement, PasswordFieldProps>(
  ({ hideLabel, showLabel, ...props }, forwardedRef) => {
    const [isRevealed, setIsRevealed] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    React.useImperativeHandle(
      forwardedRef,
      () => inputRef.current as HTMLInputElement,
    );

    const toggleVisibility = () => {
      const input = inputRef.current;
      const selectionStart = input?.selectionStart ?? null;
      const selectionEnd = input?.selectionEnd ?? null;

      setIsRevealed((current) => !current);
      requestAnimationFrame(() => {
        input?.focus();
        if (selectionStart !== null && selectionEnd !== null) {
          input?.setSelectionRange(selectionStart, selectionEnd);
        }
      });
    };

    const toggleLabel = isRevealed ? hideLabel : showLabel;

    return (
      <Input
        {...props}
        ref={inputRef}
        type={isRevealed ? "text" : "password"}
        rightIcon={
          <button
            type="button"
            aria-label={toggleLabel}
            aria-pressed={isRevealed}
            className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring active:bg-accent inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-sm transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed motion-reduce:transition-none forced-colors:border forced-colors:border-[ButtonBorder]"
            onClick={toggleVisibility}
          >
            <Icon name={isRevealed ? "EyeOff" : "Eye"} />
          </button>
        }
      />
    );
  },
);
PasswordField.displayName = "PasswordField";

export interface SearchFieldProps extends Omit<
  InputProps,
  "defaultValue" | "leftIcon" | "onChange" | "rightIcon" | "type" | "value"
> {
  clearLabel: string;
  debounceMs?: number;
  onClear?: () => void;
  onSearch?: (
    value: string,
    context: { signal: AbortSignal },
  ) => Promise<unknown> | unknown;
  onValueChange: (value: string) => void;
  resultCountLabel?: React.ReactNode;
  value: string;
}

const SearchField = React.forwardRef<HTMLInputElement, SearchFieldProps>(
  (
    {
      clearLabel,
      debounceMs = 300,
      onClear,
      onCompositionEnd,
      onCompositionStart,
      onSearch,
      onValueChange,
      resultCountLabel,
      value,
      ...props
    },
    forwardedRef,
  ) => {
    const [isComposing, setIsComposing] = React.useState(false);
    const [isSearching, setIsSearching] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const requestRef = React.useRef<AbortController | null>(null);
    const requestSequenceRef = React.useRef(0);
    const skipNextValueRef = React.useRef<string | null>(null);

    React.useImperativeHandle(
      forwardedRef,
      () => inputRef.current as HTMLInputElement,
    );

    const dispatchSearch = React.useCallback(
      (query: string) => {
        if (!onSearch) return;

        requestRef.current?.abort();
        const controller = new AbortController();
        const sequence = ++requestSequenceRef.current;
        requestRef.current = controller;
        setIsSearching(true);

        const finishSearch = () => {
          if (requestSequenceRef.current === sequence) {
            setIsSearching(false);
          }
        };

        try {
          const result = onSearch(query, { signal: controller.signal });
          if (
            result &&
            typeof (result as PromiseLike<unknown>).then === "function"
          ) {
            Promise.resolve(result)
              .catch(() => undefined)
              .finally(finishSearch);
          } else {
            finishSearch();
          }
        } catch {
          finishSearch();
        }
      },
      [onSearch],
    );

    React.useEffect(() => {
      if (!onSearch || isComposing) return undefined;
      if (skipNextValueRef.current === value) {
        skipNextValueRef.current = null;
        return undefined;
      }

      const timeout = window.setTimeout(
        () => dispatchSearch(value),
        debounceMs,
      );
      return () => window.clearTimeout(timeout);
    }, [debounceMs, dispatchSearch, isComposing, onSearch, value]);

    React.useEffect(
      () => () => {
        requestRef.current?.abort();
      },
      [],
    );

    const clearSearch = () => {
      requestRef.current?.abort();
      requestSequenceRef.current += 1;
      setIsSearching(false);
      skipNextValueRef.current = "";
      onValueChange("");
      onClear?.();
      dispatchSearch("");
      inputRef.current?.focus();
    };

    return (
      <div className="w-full">
        <Input
          {...props}
          ref={inputRef}
          type="search"
          value={value}
          aria-busy={isSearching || undefined}
          leftIcon={
            <Icon
              name={isSearching ? "Loader2" : "Search"}
              className={cn(
                isSearching && "animate-spin motion-reduce:animate-none",
              )}
            />
          }
          rightIcon={
            value ? (
              <button
                type="button"
                aria-label={clearLabel}
                className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring active:bg-accent inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-sm transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none forced-colors:border forced-colors:border-[ButtonBorder]"
                onClick={clearSearch}
              >
                <Icon name="X" />
              </button>
            ) : undefined
          }
          onChange={(event) => onValueChange(event.currentTarget.value)}
          onCompositionStart={(event) => {
            requestRef.current?.abort();
            requestSequenceRef.current += 1;
            setIsSearching(false);
            setIsComposing(true);
            onCompositionStart?.(event);
          }}
          onCompositionEnd={(event) => {
            setIsComposing(false);
            onValueChange(event.currentTarget.value);
            onCompositionEnd?.(event);
          }}
        />
        {resultCountLabel ? (
          <output
            aria-live="polite"
            className="text-muted-foreground mt-1 block min-h-5 text-sm"
          >
            {resultCountLabel}
          </output>
        ) : null}
      </div>
    );
  },
);
SearchField.displayName = "SearchField";

const TextField = Input;

const focusFirstInvalidField = (form: HTMLFormElement) => {
  const firstInvalidField = form.querySelector<HTMLElement>(
    '[aria-invalid="true"]:not(:disabled)',
  );

  firstInvalidField?.focus();
  firstInvalidField?.scrollIntoView?.({ block: "center", behavior: "auto" });
  return firstInvalidField;
};

export {
  focusFirstInvalidField,
  FormField,
  PasswordField,
  SearchField,
  TextArea,
  TextField,
};
